/**
 * Space Alert Settings Page
 *
 * Allows Admins to configure space-specific alert overrides.
 * Each tool is a collapsible card with Admin/Manager columns side by side.
 * Uses same layout as company alerts for consistency.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useSpaceAccess } from '@/hooks/usePermissions';
import { AccessDenied } from '@/components/AccessDenied';
import { AppShell3 } from '@/components/app-shell-3';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { AlertSettingsEditor, AlertSettingsSummary } from '@/components/AlertSettingsEditor';
import { PageTransition } from '@/components/PageTransition';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Save,
  Calendar,
  CheckSquare,
  MapPin,
  Award,
  Users,
  Shield,
  Grid3X3,
  CalendarDays,
  MessageSquare,
  FileText,
  ArrowLeft,
  Info,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Building2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ALERT_DEFINITIONS } from '@/config/alertDefinitions';

// Map icon strings to Lucide components
const ICON_MAP = {
  Calendar: Calendar,
  CheckSquare: CheckSquare,
  MapPin: MapPin,
  Award: Award,
  Users: Users,
  Shield: Shield,
  Grid3X3: Grid3X3,
  'Grid3x3': Grid3X3,
  CalendarDays: CalendarDays,
  MessageSquare: MessageSquare,
  FileText: FileText
};

export function SpaceAlertSettingsPage() {
  const { projectId } = useParams();
  const { userProfile, company } = useAuth();
  const { canManage: canEditSpace, loading: permissionLoading } = useSpaceAccess(projectId);

  usePageTitle('Space Alerts');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [space, setSpace] = useState(null);

  // Track which tools are expanded
  const [expandedTools, setExpandedTools] = useState(['bookings', 'qualifications']);

  // Alert settings state
  const [adminSettings, setAdminSettings] = useState({});
  const [managerSettings, setManagerSettings] = useState({});

  // Company defaults for reset
  const [companyDefaults, setCompanyDefaults] = useState({ admin: {}, manager: {} });

  // Company config for thresholds (read-only)
  const [companyConfig, setCompanyConfig] = useState({
    critical_shortage_threshold: 3,
    expiry_warning_days: [30, 14, 7]
  });

  useEffect(() => {
    if (permissionLoading) return;

    if (!canEditSpace) {
      toast.error('You do not have permission to access this page');
      return;
    }

    loadSpaceAndSettings();
  }, [projectId, company?.id, canEditSpace, permissionLoading]);

  const loadSpaceAndSettings = async () => {
    if (!projectId || !company?.id) return;

    try {
      setLoading(true);

      // Load all data in parallel
      const [spaceResult, companySettingsResult, companyConfigResult, spaceSettingsResult] = await Promise.all([
        supabase.from('projects').select('*').eq('id', projectId).single(),
        supabase.from('company_alert_settings').select('tool_id, role, settings').eq('company_id', company.id),
        supabase.from('company_alert_config').select('*').eq('company_id', company.id).single(),
        supabase.from('space_alert_settings').select('tool_id, role, settings').eq('project_id', projectId)
      ]);

      if (spaceResult.error) throw spaceResult.error;
      setSpace(spaceResult.data);

      // Organize company defaults
      const companyAdminDefaults = {};
      const companyManagerDefaults = {};

      // Initialize with ALERT_DEFINITIONS defaults
      Object.entries(ALERT_DEFINITIONS).forEach(([toolId, toolDef]) => {
        companyAdminDefaults[toolId] = {};
        companyManagerDefaults[toolId] = {};

        Object.entries(toolDef.alerts).forEach(([alertId, alertDef]) => {
          companyAdminDefaults[toolId][alertId] = { ...alertDef.defaults.Admin };
          companyManagerDefaults[toolId][alertId] = { ...alertDef.defaults.Manager };
        });
      });

      // Override with company settings
      if (companySettingsResult.data) {
        companySettingsResult.data.forEach((setting) => {
          if (setting.role === 'Admin') {
            companyAdminDefaults[setting.tool_id] = setting.settings;
          } else if (setting.role === 'Manager') {
            companyManagerDefaults[setting.tool_id] = setting.settings;
          }
        });
      }

      setCompanyDefaults({
        admin: companyAdminDefaults,
        manager: companyManagerDefaults
      });

      // Load company config for thresholds
      if (companyConfigResult.data) {
        setCompanyConfig({
          critical_shortage_threshold: companyConfigResult.data.critical_shortage_threshold ?? 3,
          expiry_warning_days: companyConfigResult.data.expiry_warning_days ?? [30, 14, 7]
        });
      }

      // Initialize with company defaults, then apply space overrides
      const adminSettingsMap = JSON.parse(JSON.stringify(companyAdminDefaults));
      const managerSettingsMap = JSON.parse(JSON.stringify(companyManagerDefaults));

      if (spaceSettingsResult.data) {
        spaceSettingsResult.data.forEach((setting) => {
          if (setting.role === 'Admin') {
            adminSettingsMap[setting.tool_id] = setting.settings;
          } else if (setting.role === 'Manager') {
            managerSettingsMap[setting.tool_id] = setting.settings;
          }
        });
      }

      setAdminSettings(adminSettingsMap);
      setManagerSettings(managerSettingsMap);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading space alert settings:', error);
      toast.error('Failed to load space alert settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSettingsChange = (toolId, newSettings) => {
    setAdminSettings(prev => ({
      ...prev,
      [toolId]: newSettings
    }));
    setHasChanges(true);
  };

  const handleManagerSettingsChange = (toolId, newSettings) => {
    setManagerSettings(prev => ({
      ...prev,
      [toolId]: newSettings
    }));
    setHasChanges(true);
  };

  const handleResetToolToDefault = (toolId) => {
    setAdminSettings(prev => ({
      ...prev,
      [toolId]: JSON.parse(JSON.stringify(companyDefaults.admin[toolId]))
    }));
    setManagerSettings(prev => ({
      ...prev,
      [toolId]: JSON.parse(JSON.stringify(companyDefaults.manager[toolId]))
    }));
    setHasChanges(true);
    toast.success(`Reset ${ALERT_DEFINITIONS[toolId]?.name || toolId} to company defaults`);
  };

  const toggleTool = (toolId) => {
    setExpandedTools(prev =>
      prev.includes(toolId)
        ? prev.filter(t => t !== toolId)
        : [...prev, toolId]
    );
  };

  const handleSave = async () => {
    if (!projectId || !userProfile?.id) return;

    setSaving(true);
    try {
      const records = [];

      Object.entries(adminSettings).forEach(([toolId, settings]) => {
        if (settings) {
          records.push({
            project_id: projectId,
            tool_id: toolId,
            role: 'Admin',
            settings: settings,
            configured_by: userProfile.id
          });
        }
      });

      Object.entries(managerSettings).forEach(([toolId, settings]) => {
        if (settings) {
          records.push({
            project_id: projectId,
            tool_id: toolId,
            role: 'Manager',
            settings: settings,
            configured_by: userProfile.id
          });
        }
      });

      const { error } = await supabase
        .from('space_alert_settings')
        .upsert(records, { onConflict: 'project_id,tool_id,role' });

      if (error) throw error;

      toast.success('Space alert settings saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving space alert settings:', error);
      toast.error('Failed to save space alert settings');
    } finally {
      setSaving(false);
    }
  };

  if (permissionLoading) {
    return (
      <AppShell3>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">Checking permissions...</div>
        </div>
      </AppShell3>
    );
  }

  if (!canEditSpace) {
    return (
      <AppShell3>
        <AccessDenied
          message="You don't have permission to manage alert settings for this space."
          backTo={`/space/${projectId}`}
          backLabel="Back to Space"
        />
      </AppShell3>
    );
  }

  if (loading) {
    return (
      <AppShell3>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="min-h-screen bg-gray-50 pb-24"
        >
          <div className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="h-9 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gray-200 rounded-lg animate-pulse"><div className="h-6 w-6" /></div>
                <div className="space-y-2">
                  <div className="h-7 bg-gray-200 rounded w-64 animate-pulse" />
                  <div className="h-5 bg-gray-200 rounded w-48 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="space-y-2 flex-1">
                      <div className="h-5 bg-gray-200 rounded w-32 animate-pulse" />
                      <div className="h-4 bg-gray-200 rounded w-48 animate-pulse" />
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </motion.div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center gap-4 mb-4">
                <Link to={`/space/${projectId}`}>
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Space
                  </Button>
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <Bell className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Space Alerts</h1>
                    <p className="text-gray-600 mt-1">{space?.name || 'Loading...'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-medium mb-1">Space-Specific Overrides</p>
                  <p className="text-amber-700">
                    These settings override company defaults for this space only.
                    Threshold settings are configured at the company level.
                  </p>
                </div>
              </div>
            </div>

            {/* Company Thresholds Info */}
            <Card className="bg-gray-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Company Threshold Settings</p>
                    <p className="text-xs text-muted-foreground">
                      Expiry warnings: {companyConfig.expiry_warning_days.join(', ')} days •
                      Critical shortage: {companyConfig.critical_shortage_threshold} members
                    </p>
                  </div>
                  <Link to="/company-settings/alerts">
                    <Button variant="outline" size="sm">Edit Company Settings</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Tool-based Alert Configuration */}
            <div className="space-y-4">
              {Object.entries(ALERT_DEFINITIONS).map(([toolId, toolDef]) => {
                const ToolIcon = ICON_MAP[toolDef.icon];
                const isExpanded = expandedTools.includes(toolId);

                return (
                  <Card key={toolId} className="overflow-hidden">
                    <Collapsible open={isExpanded} onOpenChange={() => toggleTool(toolId)}>
                      <CollapsibleTrigger className="w-full">
                        <CardHeader className="cursor-pointer hover:bg-gray-50/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {ToolIcon && (
                                <div className="p-2 bg-gray-100 rounded-lg">
                                  <ToolIcon className="h-5 w-5 text-gray-600" />
                                </div>
                              )}
                              <div className="text-left">
                                <CardTitle className="text-base">{toolDef.name}</CardTitle>
                                <CardDescription className="text-sm">{toolDef.description}</CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {!isExpanded && (
                                <AlertSettingsSummary
                                  toolName={toolDef.name}
                                  adminSettings={adminSettings[toolId]}
                                  managerSettings={managerSettings[toolId]}
                                  alerts={toolDef.alerts}
                                />
                              )}
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="pt-0 border-t">
                          <div className="flex justify-end mb-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetToolToDefault(toolId)}
                              className="text-xs"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reset to Company Defaults
                            </Button>
                          </div>
                          <AlertSettingsEditor
                            toolId={toolId}
                            toolName={toolDef.name}
                            toolDescription={toolDef.description}
                            toolIcon={ToolIcon}
                            alerts={toolDef.alerts}
                            adminSettings={adminSettings[toolId]}
                            managerSettings={managerSettings[toolId]}
                            onAdminChange={(newSettings) => handleAdminSettingsChange(toolId, newSettings)}
                            onManagerChange={(newSettings) => handleManagerSettingsChange(toolId, newSettings)}
                          />
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Floating Save Button */}
          <AnimatePresence>
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 20 }}
                transition={{ duration: 0.2 }}
                className="fixed bottom-6 right-6 z-50"
              >
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  size="lg"
                  className="shadow-lg hover:shadow-xl transition-shadow"
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Space Alert Settings
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
